import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'celey:appLocation';

const STATE_ABBR = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA',
  'Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS',
  'Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA',
  'Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT',
  'Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM',
  'New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK',
  'Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC',
  'South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT',
  'Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY'
};

const EMPTY_LOCATION = { city: '', state: '', zip: '', lat: null, lng: null, source: null };

// ── Geocoded-city normalization ─────────────────────────────────────────────
// Geocoders (BigDataCloud, etc.) sometimes put admin-area labels into the
// `city` field — "District 4", "Ward 8", "Census Tract 14.02", "Saint Tammany
// Parish", etc. Customers don't think in those terms and no vendor stores
// them, so we reject them and fall through to a more useful value.
//
// Rules mirror backend `utils/location_normalize.py` exactly — keep in sync.
const REJECT_CITY_PATTERNS = [
  /^(District|Ward|Precinct|Subdistrict|Zone|Sector)\s+\d+\b/i,
  /^Council\s+District\b/i,
  /^Census\s+(Tract|Block(\s+Group)?|Designated\s+Place)\b/i,
  /^Unincorporated\b/i,
  /\s+(Parish|County|Borough|Township)$/i,
  /^(Unknown|N\/A|Unspecified|None)$/i,
  /^\d+$/,
];

export const isAcceptableCityName = (name) => {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  return !REJECT_CITY_PATTERNS.some((p) => p.test(trimmed));
};

// Resolution cascade:
//   1. data.city                                  (if acceptable)
//   2. data.locality                              (if acceptable)
//   3. data.localityInfo.administrative[]         first city/municipality-level
//                                                  entry whose name is acceptable
//   4. ""                                         (UI falls back to state / "your area")
export const normalizeGeocodedCity = (data) => {
  if (!data || typeof data !== 'object') return '';
  if (isAcceptableCityName(data.city)) return data.city.trim();
  if (isAcceptableCityName(data.locality)) return data.locality.trim();

  const admin = data.localityInfo && Array.isArray(data.localityInfo.administrative)
    ? data.localityInfo.administrative
    : [];
  // BigDataCloud's `adminLevel` increases with specificity; city/municipality
  // is typically 6+. Prefer the highest-level (most specific) acceptable entry.
  const sorted = [...admin].sort((a, b) => (b?.adminLevel || 0) - (a?.adminLevel || 0));
  for (const entry of sorted) {
    if (isAcceptableCityName(entry?.name)) return entry.name.trim();
  }
  return '';
};

const LocationContext = createContext(null);

export const useAppLocation = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useAppLocation must be used within LocationProvider');
  return ctx;
};

export { STATE_ABBR };

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...EMPTY_LOCATION, ...JSON.parse(stored) } : { ...EMPTY_LOCATION };
    } catch { return { ...EMPTY_LOCATION }; }
  });
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | detecting | resolved | denied
  const detectingRef = useRef(false);
  const detectAbortRef = useRef(null);
  const detectCancelledRef = useRef(false);

  // Persist to localStorage whenever location changes
  useEffect(() => {
    if (location.source) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [location]);

  const setAppLocation = useCallback((loc) => {
    setLocation(prev => ({ ...prev, ...loc }));
    if (loc.city || loc.state || loc.zip) setGeoStatus('resolved');
  }, []);

  const clearLocation = useCallback(() => {
    setLocation({ ...EMPTY_LOCATION });
    setGeoStatus('idle');
    localStorage.removeItem(STORAGE_KEY);
    window.__celeyLocationDismissed = true;
  }, []);

  const detectLocation = useCallback(() => {
    if (!navigator.geolocation || detectingRef.current) {
      setGeoStatus('denied');
      return Promise.resolve(null);
    }
    detectingRef.current = true;
    detectCancelledRef.current = false;
    setGeoStatus('detecting');

    return new Promise((resolve) => {
      // Wrap-up helper — runs on success, error, timeout, or cancel. Safe
      // to call multiple times (guarded against double-resolve).
      let settled = false;
      const finalize = (outcome, payload = null) => {
        if (settled) return;
        settled = true;
        detectingRef.current = false;
        if (detectAbortRef.current) {
          try { detectAbortRef.current.abort(); } catch { /* noop */ }
          detectAbortRef.current = null;
        }
        if (detectCancelledRef.current) {
          // User cancelled — leave geoStatus as set by cancelDetection().
          // Discard any in-flight position payload.
          resolve(null);
          return;
        }
        if (outcome === 'resolved' && payload) {
          setLocation(payload);
          setGeoStatus('resolved');
          resolve(payload);
        } else {
          setGeoStatus('denied');
          resolve(null);
        }
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (detectCancelledRef.current) { finalize('denied'); return; }
          try {
            const { latitude, longitude } = position.coords;
            // Hard 8 s timeout on the reverse-geocode fetch — iOS WKWebView
            // on bad cellular can stall this indefinitely otherwise, which
            // is what caused the "Detecting…" hang. See audit 2026-02-28.
            const controller = new AbortController();
            detectAbortRef.current = controller;
            const fetchTimer = setTimeout(() => {
              try { controller.abort(); } catch { /* noop */ }
            }, 8000);
            let data;
            try {
              const res = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
                { signal: controller.signal }
              );
              data = await res.json();
            } finally {
              clearTimeout(fetchTimer);
            }
            if (detectCancelledRef.current) { finalize('denied'); return; }
            const city = normalizeGeocodedCity(data);
            const rawState = data.principalSubdivision || '';
            const state = STATE_ABBR[rawState] || rawState;
            const zip = data.postcode || '';
            if (city || state) {
              finalize('resolved', { city, state, zip, lat: latitude, lng: longitude, source: 'detected' });
            } else {
              finalize('denied');
            }
          } catch {
            // Fetch aborted, network error, or JSON parse error → denied
            finalize('denied');
          }
        },
        () => finalize('denied'),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });
  }, []);

  const cancelDetection = useCallback(() => {
    if (!detectingRef.current) return;
    detectCancelledRef.current = true;
    if (detectAbortRef.current) {
      try { detectAbortRef.current.abort(); } catch { /* noop */ }
      detectAbortRef.current = null;
    }
    detectingRef.current = false;
    setGeoStatus('idle');
  }, []);

  const formatDisplay = useCallback((loc) => {
    const l = loc || location;
    const parts = [];
    if (l.city) parts.push(l.city);
    if (l.state) {
      const st = l.state;
      parts.push(st.length <= 2 ? st : (STATE_ABBR[st] || st));
    }
    return parts.join(', ') || l.zip || '';
  }, [location]);

  const hasLocation = Boolean(location.city || location.state || location.zip);

  return (
    <LocationContext.Provider value={{
      location,
      geoStatus,
      hasLocation,
      isLocationDismissed: () => !!window.__celeyLocationDismissed,
      setAppLocation,
      clearLocation,
      detectLocation,
      cancelDetection,
      formatDisplay,
      STATE_ABBR
    }}>
      {children}
    </LocationContext.Provider>
  );
};
